import { Plus } from 'lucide-react';
import type { FlatWbsRow, InlineCreateState } from './types';
import type { ProjectMember } from '../../services/projectService';
import WbsTableRow from './WbsTableRow';
import WbsInlineCreate from './WbsInlineCreate';

interface Props {
  flattenedRows: FlatWbsRow[];
  bufferEnabled: boolean;
  members: ProjectMember[];
  inlineCreate: InlineCreateState | null;
  titleColWidth: number;
  hoveredRowId: string | null;
  onHoverRow: (id: string | null) => void;
  onResizeStart: (e: React.MouseEvent) => void;
  onToggle: (wbsItemId: string) => void;
  onFieldUpdate: (wbsItemId: string, field: string, value: unknown) => void;
  onStartInlineCreate: (parentId: string | null, depth: number) => void;
  onDelete: (wbsItemId: string, title: string, childCount: number) => void;
  onInlineSubmit: (data: { parentId: string | null; title: string }) => void;
  onInlineCancel: () => void;
}

export default function WbsTable({
  flattenedRows,
  bufferEnabled,
  members,
  inlineCreate,
  titleColWidth,
  hoveredRowId,
  onHoverRow,
  onResizeStart,
  onToggle,
  onFieldUpdate,
  onStartInlineCreate,
  onDelete,
  onInlineSubmit,
  onInlineCancel,
}: Props) {
  return (
    <div className="flex flex-col border-r min-w-fit">
      {/* 테이블 헤더 */}
      <div className="flex items-center border-b bg-gray-50 text-sm font-medium text-gray-500 sticky top-0 z-10 h-[42px]">
        <div
          className="relative px-3 py-2 border-r"
          style={{ width: titleColWidth, minWidth: titleColWidth, maxWidth: titleColWidth }}
        >
          항목명
          {/* 리사이즈 핸들 */}
          <div
            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-300 z-10"
            onMouseDown={onResizeStart}
          />
        </div>
        <div className="w-[60px] min-w-[60px] px-2 py-2 border-r text-center">담당자</div>
        <div className="w-[92px] min-w-[92px] px-2 py-2 border-r text-center">시작일</div>
        <div className="w-[92px] min-w-[92px] px-2 py-2 border-r text-center">종료일</div>
        <div className="w-[50px] min-w-[50px] px-2 py-2 border-r text-center leading-tight">수행<br />기간</div>
        <div className="w-[50px] min-w-[50px] px-2 py-2 border-r text-center">휴일</div>
        <div className="w-[50px] min-w-[50px] px-2 py-2 border-r text-center">보정</div>
        {bufferEnabled && (
          <>
            <div className="w-[50px] min-w-[50px] px-2 py-2 border-r text-center leading-tight">버퍼<br />기간</div>
            <div className="w-[80px] min-w-[80px] px-2 py-2 text-center leading-tight">예상<br />지연</div>
          </>
        )}
      </div>

      {/* 행 목록 */}
      <div className="flex-1 overflow-y-auto">
        {flattenedRows.map((row) => (
          <div key={row.node.wbsItemId}>
            <WbsTableRow
              node={row.node}
              isExpanded={row.isExpanded}
              hasChildren={row.hasChildren}
              bufferEnabled={bufferEnabled}
              members={members}
              titleColWidth={titleColWidth}
              isHovered={hoveredRowId === row.node.wbsItemId}
              onMouseEnter={() => onHoverRow(row.node.wbsItemId)}
              onMouseLeave={() => onHoverRow(null)}
              onToggle={() => onToggle(row.node.wbsItemId)}
              onFieldUpdate={onFieldUpdate}
              onStartInlineCreate={onStartInlineCreate}
              onDelete={onDelete}
            />
            {/* 인라인 생성 (이 행의 하위로 생성) */}
            {inlineCreate?.parentId === row.node.wbsItemId && row.isExpanded && (
              <WbsInlineCreate
                depth={inlineCreate.depth}
                titleColWidth={titleColWidth}
                onSubmit={(title) => onInlineSubmit({ parentId: inlineCreate.parentId, title })}
                onCancel={onInlineCancel}
              />
            )}
          </div>
        ))}

        {/* 비어있을 때 */}
        {flattenedRows.length === 0 && !inlineCreate && (
          <div className="flex items-center justify-center py-12 text-sm text-gray-400">
            아직 WBS 항목이 없습니다. 아래에서 진행 단계를 추가해보세요.
          </div>
        )}
      </div>

      {/* 하단: 진행 단계 추가 */}
      <div className="border-t px-3 py-2 bg-white sticky bottom-0">
        {inlineCreate?.parentId === null ? (
          <WbsInlineCreate
            depth={1}
            titleColWidth={titleColWidth}
            onSubmit={(title) => onInlineSubmit({ parentId: null, title })}
            onCancel={onInlineCancel}
          />
        ) : (
          <button
            onClick={() => onStartInlineCreate(null, 1)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 px-2 py-1.5 rounded transition-colors"
          >
            <Plus size={14} />
            <span>진행 단계 추가</span>
          </button>
        )}
      </div>
    </div>
  );
}
